import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X, Bell } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, duration = 5000) => {
      const id = Math.random().toString(36).substring(2, 9);
      const toast: Toast = { id, type, message, duration };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <div className="fixed top-6 right-6 z-9999 flex flex-col gap-3 pointer-events-none w-full max-w-[400px]">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!toast.duration) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / toast.duration!) * 100);
      setProgress(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [toast.duration]);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const config = {
    success: {
      bg: 'bg-emerald-50/90',
      border: 'border-emerald-200/50',
      icon: CheckCircle2,
      color: 'text-emerald-600',
      progress: 'bg-emerald-500',
      shadow: 'shadow-emerald-500/10',
    },
    error: {
      bg: 'bg-rose-50/90',
      border: 'border-rose-200/50',
      icon: XCircle,
      color: 'text-rose-600',
      progress: 'bg-rose-500',
      shadow: 'shadow-rose-500/10',
    },
    warning: {
      bg: 'bg-amber-50/90',
      border: 'border-amber-200/50',
      icon: AlertTriangle,
      color: 'text-amber-600',
      progress: 'bg-amber-500',
      shadow: 'shadow-amber-500/10',
    },
    info: {
      bg: 'bg-indigo-50/90',
      border: 'border-indigo-200/50',
      icon: Info,
      color: 'text-indigo-600',
      progress: 'bg-indigo-500',
      shadow: 'shadow-indigo-500/10',
    },
  }[toast.type];

  const Icon = config.icon;

  return (
    <div
      className={`
        pointer-events-auto relative group overflow-hidden
        ${config.bg} ${config.border} border-2 backdrop-blur-xl
        p-4 rounded-[22px] flex items-start gap-4
        transition-all duration-300 ease-out
        animate-in fade-in slide-in-from-right-8
        ${isExiting ? 'animate-out fade-out slide-out-to-right-8 scale-95' : 'hover:scale-[1.02]'}
        ${config.shadow} shadow-2xl
      `}
    >
      <div
        className={`
        shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center 
        ${config.bg} border-2 ${config.border} shadow-inner
      `}
      >
        <Icon className={`w-6 h-6 ${config.color}`} />
      </div>

      <div className="flex-1 pt-1">
        <div className="flex items-center gap-2 mb-0.5">
          <Bell className={`w-3 h-3 ${config.color} opacity-40`} />
          <span
            className={`text-[10px] font-black uppercase tracking-widest ${config.color} opacity-40`}
          >
            System Notification
          </span>
        </div>
        <p className="text-sm font-bold text-slate-800 leading-tight">{toast.message}</p>
      </div>

      <button
        onClick={handleRemove}
        className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-black/5 transition-colors"
      >
        <X className="w-4 h-4 text-slate-400" />
      </button>

      {/* Progress Bar Timer */}
      {toast.duration && toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 w-full h-1 bg-black/5 overflow-hidden">
          <div
            className={`h-full ${config.progress} transition-all duration-10 linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
